import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { PageLayout } from '@/shared/layout/PageLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useAuth } from '@/core/auth/AuthContext';
import { usePermission } from '@/core/permissions/usePermission';
import { useTeamEdit } from './hooks/useTeamEdit';
import type { TeamEditFormValues } from './types/teamEdit.types';

export default function TeamsEditPage() {
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const { can } = usePermission();
  const canDelete = can('teams' as any, 'delete' as any);
  const { referenceData, initialData, isLoading, isSubmitting, updateTeam } = useTeamEdit(companyId, id);

  const [form, setForm] = useState<TeamEditFormValues | null>(null);

  useEffect(() => {
    if (initialData) {
      setForm(initialData);
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !form.name || !form.code) return;
    try {
      await updateTeam(form);
      navigate(`/teams/${id}`);
    } catch (err) {
      console.error('Failed to update team', err);
    }
  };

  if (isLoading) return <div className="p-10 text-center animate-pulse text-slate-400 sm:text-sm text-xs">Đang tải thông tin đội nhóm...</div>;
  if (!form) return <div className="p-10 text-center text-slate-500 sm:text-sm text-xs">Không tìm thấy thông tin.</div>;

  return (
    <PageLayout
      title={`Chỉnh sửa: ${form.name}`}
      description="Cập nhật thông tin đội nhóm và phân công nhiệm vụ"
      headerActions={
        <Button variant="outline" onClick={() => navigate(-1)} className="h-9">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Quay lại
        </Button>
      }
    >
      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto space-y-6">
        <div className="bg-white rounded-md border border-slate-100 p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Tên đội nhóm"
              placeholder="VD: Đội thi công số 1"
              required
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
            />
            <Input
              label="Mã định danh"
              placeholder="VD: TC-01"
              value={form.code}
              readOnly
            />
            <Select
              label="Loại hình dịch vụ"
              options={[
                { value: 'construction', label: 'Thi công' },
                { value: 'maintenance', label: 'Bảo dưỡng' },
                { value: 'both', label: 'Cả hai' }
              ]}
              value={form.service_category}
              onValueChange={val => setForm({ ...form, service_category: val as any })}
            />
            <Select
              label="Loại team"
              placeholder="Chọn loại team"
              options={[
                { value: 'baoduong', label: 'Bảo dưỡng' },
                { value: 'thicong', label: 'Thi công' },
                { value: 'vuonuom', label: 'Vườn ươm' },
                { value: 'cogioi', label: 'Cơ giới' },
                { value: 'khac', label: 'Khác' }
              ]}
              value={form.team_type}
              onValueChange={val => setForm({ ...form, team_type: val })}
            />
            <Select
              label="Trạng thái hoạt động"
              options={[
                { value: 'active', label: 'Hoạt động' },
                { value: 'inactive', label: 'Tạm ngưng' },
                { value: 'on_leave', label: 'Giải thể' }
              ]}
              value={form.status_code}
              onValueChange={val => setForm({ ...form, status_code: val as any })}
            />
            <Input
              label="Năng lực (Công/ngày)"
              type="number"
              value={form.base_capacity}
              onChange={e => setForm({ ...form, base_capacity: Number(e.target.value) })}
            />
            <Select
              label="Dự án phụ trách"
              options={[{ value: '', label: 'Chưa phân công' }, ...referenceData.projects]}
              value={form.project_id || ''}
              onValueChange={val => setForm({ ...form, project_id: val })}
            />
            <Select
              label="Tổ trưởng"
              options={[{ value: '', label: 'Chưa phân công' }, ...referenceData.users]}
              value={form.leader_user_id || ''}
              onValueChange={val => setForm({ ...form, leader_user_id: val })}
            />
            <Select
              label="Tổ phó"
              options={[{ value: '', label: 'Chưa phân công' }, ...referenceData.users]}
              value={form.vice_leader_user_id || ''}
              onValueChange={val => setForm({ ...form, vice_leader_user_id: val })}
            />
            <Select
              label="Hợp đồng liên quan"
              placeholder="Chọn hợp đồng"
              options={referenceData.contracts}
              value={form.contract_id || ''}
              onValueChange={val => setForm({ ...form, contract_id: val })}
            />
          </div>

          <div className="space-y-1.5">
            <label className="sm:text-sm text-xs text-slate-400 font-normal tracking-widest">Ghi chú / Mô tả</label>
            <textarea
              className="w-full h-32 p-3 bg-slate-50 border border-slate-100 rounded-md sm:text-sm text-xs outline-none focus:ring-1 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all"
              placeholder="Nhập mô tả chi tiết về đội nhóm..."
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </div>

        <div className="flex justify-between items-center pt-4">
          <div>
            {canDelete && (
              <Button type="button" variant="ghost" onClick={() => navigate('/teams')} className="h-10 text-rose-600 hover:bg-rose-50 border-none">
                <Trash2 className="w-4 h-4 mr-2" />
                Giải thể team
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => navigate(-1)} disabled={isSubmitting} className="h-10 px-6">
              Hủy bỏ
            </Button>
            <Button type="submit" isLoading={isSubmitting} className="h-10 px-8 bg-emerald-800 hover:bg-emerald-900 border-none">
              <Save className="w-4 h-4 mr-2" />
              Cập nhật thay đổi
            </Button>
          </div>
        </div>
      </form>
    </PageLayout>
  );
}
